// pages/Gobernanta.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Badge,
  Statistic,
  Card,
  Divider,
  DatePicker,
  Switch,
  Tabs,
  Descriptions,
  Empty
} from 'antd';
import {
  PrinterOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  FilterOutlined,
  FilePdfOutlined,
  HomeOutlined,
  TeamOutlined,
  RiseOutlined,
  FallOutlined,
  SwapOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  CoffeeOutlined,
  GiftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { useReactToPrint } from 'react-to-print';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';
import './ListadoGobernanta.css'; // Importamos estilos personalizados

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

// Componente para la vista de impresión
const ReporteGobernanta = React.forwardRef(({ datos, filtros, estadisticas, hotelInfo }, ref) => {
  // Calcular cambios de sábanas y toallas según la lógica del FastReport

  const calcularCambio = (fechaEntrada, fechaReporte, intervalo) => {
    if (!fechaEntrada || !fechaReporte || intervalo === 0) return false;

    const diffDays = dayjs(fechaReporte).diff(dayjs(fechaEntrada), 'days');

    // console.log(" dias => ",diffDays);
    
    return diffDays % intervalo === 0;
  };

  // Colores según el tipo (igual que en FastReport)
  const getBackgroundColor = (item) => {
    if (item.esVIP) return '#FFE4E1'; // $008080FF en FastReport (salmon claro)
    if (item.estado === 'SALIDA') return '#F0F0F0'; // $00CFCFCF en FastReport (gris claro)
    return 'transparent';
  };

  // Totales
  const totales = {
    salidas: datos.filter(d => d.estado === 'SALIDA').length,
    entradas: datos.filter(d => d.estado === 'ENTRADA').length,
    ocupadas: datos.filter(d => d.estado === 'OCUPADA').length,
    libres: datos.filter(d => d.estado === 'LIBRE').length,
    bloqueadas: datos.filter(d => d.estado === 'BLOQUEADA').length
  };

  return (
    <div className="reporte-gobernanta" ref={ref}>
      {/* Cabecera del reporte */}
      <div className="reporte-header">
        <div className="header-superior">
          <h1 className="hotel-nombre">{hotelInfo?.nombre || 'HOTEL'}</h1>
          <div className="fecha-pagina">
            <div>Fecha: {dayjs(filtros.fecha).format('DD/MM/YYYY')}</div>
            <div>Página: 1</div>
          </div>
        </div>
        
        <h2 className="titulo-reporte">LISTADO DE GOBERNANTA</h2>
        <h3 className="subtitulo-reporte">Día: {dayjs(filtros.fecha).format('DD/MM/YYYY')}</h3>
        
        <div className="linea-divisoria"></div>
        
        {/* Cabecera de columnas */}
        <div className="cabecera-columnas">
          <div className="col-hab">Hab</div>
          <div className="col-hesp">H.Esp.</div>
          <div className="col-estado">Estado</div>
          <div className="col-entrada">Entrada</div>
          <div className="col-salida">Salida</div>
          <div className="col-observaciones">Observaciones</div>
          <div className="col-thab">THab</div>
          <div className="col-pax">PAX</div>
          <div className="col-sabanas" title="Cambio Sábanas">Sábanas</div>
          <div className="col-toallas" title="Cambio Toallas">Toallas</div>
        </div>
      </div>

      {/* Cuerpo del reporte */}
      <div className="reporte-cuerpo">
        {datos.map((item, index) => {
          const bgColor = getBackgroundColor(item);
          const mostrarCambioSabanas = item.estado === 'SALIDA' ? true :
                                      item.estado === 'ENTRADA' ? false :
                                      item.estado === 'LIBRE' ? false :
                                      calcularCambio(item.entrada, filtros.fecha, filtros.cambiarSabanas);
          
          const mostrarCambioToallas = item.estado === 'SALIDA' ? true :
                                      item.estado === 'ENTRADA' ? false :
                                      item.estado === 'LIBRE' ? false :
                                      calcularCambio(item.entrada, filtros.fecha, filtros.cambiarToallas);

          return (
            <div 
              key={`${item.numHab}-${index}`} 
              className="fila-dato"
              style={{ backgroundColor: bgColor }}
            >
              <div className="col-hab"><strong>{item.numHab}</strong></div>
              <div className="col-hesp">
                {item.habitacionEspecial && (
                  <Tag color="gold" size="small">{item.habitacionEspecial}</Tag>
                )}
              </div>
              <div className="col-estado">{item.estado}</div>
              <div className="col-entrada">
                {item.entrada ? dayjs(item.entrada).format('DD/MM/YYYY') : ''}
              </div>
              <div className="col-salida">
                {item.salida ? dayjs(item.salida).format('DD/MM/YYYY') : ''}
              </div>
              <div className="col-observaciones">
                {item.observaciones || item.nombreHuesped || ''}
              </div>
              <div className="col-thab">{item.tipoHab || item.ocupantes || ''}</div>
              <div className="col-pax">{item.ocupantes || item.estado}</div>
              <div className="col-sabanas">
                {mostrarCambioSabanas && <span className="marca-cambio sabanas">X</span>}
              </div>
              <div className="col-toallas">
                {mostrarCambioToallas && <span className="marca-cambio toallas">X</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pie con totales */}
      <div className="reporte-footer">
        <div className="linea-divisoria"></div>
        <div className="totales">
          <div className="total-item">
            <span className="total-label">Salidas + Ocupadas:</span>
            <span className="total-valor">{totales.salidas + totales.ocupadas}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Salidas:</span>
            <span className="total-valor">{totales.salidas}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Entradas:</span>
            <span className="total-valor">{totales.entradas}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Ocupadas:</span>
            <span className="total-valor">{totales.ocupadas}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Libres:</span>
            <span className="total-valor">{totales.libres}</span>
          </div>
          <div className="total-item">
            <span className="total-label">Bloqueadas:</span>
            <span className="total-valor">{totales.bloqueadas}</span>
          </div>
        </div>
      </div>

      {/* Estilos incrustados para la impresión */}
      <style>{`
        @media print {
          .reporte-gobernanta {
            font-family: Arial, sans-serif;
            font-size: 10pt;
            width: 100%;
          }
          .reporte-header {
            margin-bottom: 10px;
          }
          .header-superior {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }
          .hotel-nombre {
            font-size: 18pt;
            font-weight: bold;
            margin: 0;
          }
          .fecha-pagina {
            text-align: right;
            font-size: 10pt;
          }
          .titulo-reporte {
            text-align: center;
            font-size: 16pt;
            font-weight: bold;
            margin: 5px 0;
          }
          .subtitulo-reporte {
            text-align: center;
            font-size: 14pt;
            margin: 5px 0;
          }
          .linea-divisoria {
            border-top: 2px solid black;
            margin: 5px 0;
          }
          .cabecera-columnas {
            display: flex;
            font-weight: bold;
            border-bottom: 1px solid black;
            padding: 5px 0;
          }
          .fila-dato {
            display: flex;
            padding: 2px 0;
            border-bottom: 1px dotted #ccc;
          }
          .col-hab { width: 40px; }
          .col-hesp { width: 45px; }
          .col-estado { width: 80px; }
          .col-entrada { width: 75px; }
          .col-salida { width: 75px; }
          .col-observaciones { width: 200px; }
          .col-thab { width: 60px; }
          .col-pax { width: 40px; }
          .col-sabanas { width: 45px; text-align: center; }
          .col-toallas { width: 45px; text-align: center; }
          .marca-cambio {
            display: inline-block;
            width: 16px;
            height: 16px;
            font-weight: bold;
          }
          .marca-cambio.sabanas { color: red; }
          .marca-cambio.toallas { color: green; }
          .totales {
            display: flex;
            gap: 20px;
            margin-top: 10px;
          }
          .total-item {
            font-size: 9pt;
          }
          .total-label {
            font-weight: bold;
            margin-right: 5px;
          }
        }
      `}</style>
    </div>
  );
});

const ListadoGobernanta = () => {
  // Refs para impresión
  const reporteRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => reporteRef.current,
    documentTitle: `Gobernanta_${dayjs().format('YYYYMMDD_HHmm')}`,
    onAfterPrint: () => message.success('Reporte enviado a impresora')
  });

  // Estados
  const [listado, setListado] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [estadisticas, setEstadisticas] = useState(null);
  const [activeTab, setActiveTab] = useState('listado');
  const [hotelInfo, setHotelInfo] = useState({ nombre: 'HOTEL' });
  
  const [sortedData, setSortedData] = useState([]);
  const [sortInfo, setSortInfo] = useState({ field: 'NumHab', order: 'ascend' });  
  
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    fecha: dayjs().format('YYYY-MM-DD'),
    cambiarSabanas: 3,
    cambiarToallas: 2,
    mostrarRangoGobernanta: true,
    mostrarANB: true,
    mostrarNombreHuesped: true,
    saltarPorPiso: true,
    mostrarHabitacionesLibres: true,
    tipoListado: 0,
    codigoInforme: null
  });

  // Estados para filtros de visualización
  const [filtrosVisualizacion, setFiltrosVisualizacion] = useState({
    piso: 'todos',
    estado: 'todos',
    rango: 'todos'
  });

  // Estado para informes disponibles
  const [informesDisponibles, setInformesDisponibles] = useState([]);

  // Estado para pisos y rangos disponibles
  const [pisosDisponibles, setPisosDisponibles] = useState([]);
  const [rangosDisponibles, setRangosDisponibles] = useState([]);

  // Modal de detalles
  const [detalleVisible, setDetalleVisible] = useState(false);
  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState(null);

  // Cargar datos iniciales
  useEffect(() => {
    cargarInformesDisponibles();
    generarListado();
    cargarHotelInfo();
  }, []);

  const cargarHotelInfo = () => {
    // Aquí cargarías la información del hotel desde tu contexto/API
    setHotelInfo({ nombre: localStorage.getItem('hotelNombre') || 'HOTEL' });
  };

  // Actualizar pisos y rangos cuando cambia el listado
  useEffect(() => {
    if (listado.length > 0) {
      const pisos = [...new Set(listado.map(item => item.piso).filter(Boolean))].sort();
      const rangos = [...new Set(listado.map(item => item.rango).filter(Boolean))].sort();
      setPisosDisponibles(pisos);
      setRangosDisponibles(rangos);
    }
  }, [listado]);

  const cargarInformesDisponibles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/listadogobernanta/informes-disponibles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInformesDisponibles(response.data.data || []);
    } catch (error) {
      console.error('Error cargando informes:', error);
    }
  };

  const generarListado = async () => {
    setLoading(true);
    setSearchLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${BASE_URL}/api/listadogobernanta/listado`,
        filtros,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data.data;
      const listadoConObservaciones = (data.listado || []).map(item => ({
        ...item,
        // Simulamos observaciones como en el FastReport
        observaciones: item.observaciones ? 
          `${item.observaciones}` : 
          ``
      }));

      // console.log(" listadoConObservaciones => ",listadoConObservaciones);
      
      
      setListado(listadoConObservaciones);

      // const ordenados = [...listadoConObservaciones].sort((a, b) => {
      //   const field = sortInfo.field || 'NumHab';
      //   const order = sortInfo.order || 'ascend';
    
      //   if (field === 'NumHab' || field === 'pax') {
      //     return order === 'ascend' ? a[field] - b[field] : b[field] - a[field];
      //   } else {
      //     const valA = String(a[field] || '');
      //     const valB = String(b[field] || '');
      //     return order === 'ascend' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      //   }
      // });
      // setSortedData(ordenados);      
    
      const resumen = calcularEstados(listadoConObservaciones);
      
      console.log(" resumen => ",resumen);
      
      setEstadisticas(resumen);
      
      // Calcular estadísticas
      // calcularEstadisticas(listadoConObservaciones);
      
      message.success('Listado generado correctamente');
      
    } catch (error) {
      console.error('❌ Error generando listado:', error);
      message.error(error.response?.data?.error || 'Error al generar el listado');
      setListado([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const calcularEstados = (data) => {
    let entradas = 0, salidas = 0, ocupadas = 0, salidaEntrada = 0, libres = 0, bloqueada = 0, sinDefinir = 0;
  // console.log(" data => ",data);
  
    data.forEach(item => {
      // console.log(" item => ",item.Estado);
      
      switch(item.Estado) {
        case 'ENTRADA':
          entradas++;
          break;
        case 'SALIDA':
          salidas++;
          break;
        case 'Salida/Bloqueada':
          salidas++;
          break;          
        case 'OCUPADA':
          ocupadas++;
          break;
        case 'Salida/Entrada':
          salidaEntrada++;
          break;
        case 'LIBRE':
          libres++;
          break;          
        case 'BLOQUEADA':
          bloqueada++;
          break;                    
        default: 
          sinDefinir++;
          // console.log(`Estado no definido encontrado: ${item.estado}`);
          break;
      }
    });

    const totalHabitaciones = data.length;
    const entradasTotal = entradas + salidaEntrada;
    const salidasTotal = salidas + salidaEntrada;
    const ocupadasTotal = ocupadas + entradas + salidaEntrada;
    const entradasSalidasTotal = salidaEntrada;
    const libresTotal = libres + salidas;

    // const totalHabitaciones = data.length;
    // const entradasTotal = entradas;
    // const salidasTotal = salidas;
    // const ocupadasTotal = ocupadas;
    // const entradasSalidasTotal = salidaEntrada;
    // const libresTotal = libres; 

    // console.log(" Resumen => ",entradasTotal,salidasTotal,ocupadasTotal,libresTotal,entradasSalidasTotal);
    

    return {
      entradas: entradasTotal,
      salidas: salidasTotal,
      ocupadas: ocupadasTotal,
      libres: libresTotal,
      salidaEntrada: entradasSalidasTotal,
      sinDefinir: sinDefinir,
    };
  };



  const calcularEstadisticas = (datos) => {
    const stats = {
      totalHabitaciones: datos.length,
      ocupadas: datos.filter(item => item.estado === 'OCUPADA').length,
      entradas: datos.filter(item => item.estado === 'ENTRADA').length,
      salidas: datos.filter(item => item.estado === 'SALIDA').length,
      diurnas: datos.filter(item => item.estado === 'DIURNA').length,
      libres: datos.filter(item => item.estado === 'LIBRE').length,
      bloqueadas: datos.filter(item => item.estado === 'BLOQUEADA').length,
      cambios: datos.filter(item => item.estado === 'CAMBIO HABITACIÓN').length,
      vip: datos.filter(item => item.esVIP).length
    };
    
    stats.porcentajeOcupacion = datos.length > 0 
      ? ((stats.ocupadas + stats.entradas) / datos.length * 100).toFixed(1)
      : 0;
    
    setEstadisticas(stats);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleVisualizacionChange = (campo, valor) => {
    setFiltrosVisualizacion(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleVerDetalle = (habitacion) => {
    setHabitacionSeleccionada(habitacion);
    setDetalleVisible(true);
  };

  // Columnas de la tabla (estilo FastReport)
  const columns = [
    {
      title: 'Hab',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 60,
      fixed: 'left',
      sorter: (a, b) => Number(a.NumHab) - Number(b.NumHab),
      defaultSortOrder: 'ascend', // Orden ascendente por defecto
      render: (NumHab, record) => (
        <div style={{ 
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px',
          fontWeight: 'bold'
        }}>
          <span 
            style={{ cursor: 'pointer' }}
            onClick={() => handleVerDetalle(record)}
          >
            {NumHab}
            {record.esVIP && <span style={{ marginLeft: 4, color: 'gold' }}>👑</span>}
          </span>
        </div>
      ),
    },
    {
      title: 'H.Ven.',
      dataIndex: 'habitacionVenta2',
      key: 'habitacionVenta2',
      width: 120,
      sorter: (a, b) => (a.habitacionVenta2 || '').localeCompare(b.habitacionVenta2 || ''),
      render: (_, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {record.habitacionVenta2 && (
            <Tag color="gold" size="small">{record.habitacionVenta2}</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'Estado',
      key: 'Estado',
      width: 100,
      sorter: (a, b) => a.Estado - b.Estado,
      render: (Estado, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.Estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {Estado}
        </div>
      ),
    },
    {
      title: 'Entrada',
      dataIndex: 'Entrada',
      key: 'Entrada',
      width: 90,
      render: (Entrada, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {Entrada ? dayjs(Entrada).format('DD/MM/YYYY') : ''}
        </div>
      ),
    },
    {
      title: 'Salida',
      dataIndex: 'Salida',
      key: 'Salida',
      width: 90,
      render: (Salida, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {Salida ? dayjs(Salida).format('DD/MM/YYYY') : ''}
        </div>
      ),
    },
    {
      title: 'Observaciones',
      dataIndex: 'ObservacionesGobernanta',
      key: 'ObservacionesGobernanta',
      width: 250,
      ellipsis: true,
      render: (ObservacionesGobernanta, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {ObservacionesGobernanta || record.nombreHuesped || ''}
        </div>
      ),
    },
    {
      title: 'THab',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 60,
      render: (TipoHab, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {TipoHab || record.ocupantes || ''}
        </div>
      ),
    },
    {
      title: 'PAX',
      dataIndex: 'pax',
      key: 'pax',
      width: 50,
      render: (_, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          {record.pax}
        </div>
      ),
    },
    {
      title: 'Sábanas',
      key: 'cambiarSabanas',
      width: 60,
      align: 'center',
      render: (_, record) => {
        const fechaListadoDate = record.fechaListado+'T00:00:00.000Z';
        const entradaDate = record.Entrada;
      
        const mostrar = record.Estado === 'SALIDA' ? true :
                       record.Estado === 'ENTRADA' ? false :
                       record.Estado === 'LIBRE' ? false :
                       (dayjs(fechaListadoDate).diff(dayjs(entradaDate), 'days') % filtros.cambiarSabanas === 0);

                      //  console.log(dayjs(fechaListadoDate).diff(dayjs(entradaDate), 'days'));                             
        return (
          <div style={{
            backgroundColor: record.EsVIP ? '#FFE4E1' : (record.Estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
            padding: '4px',
            textAlign: 'center'
          }}>
            {mostrar && <span style={{ color: 'red', fontWeight: 'bold' }}>X</span>}
          </div>
        );
      },
    },
    {
      title: 'Toallas',
      key: 'cambiarToallas',
      width: 60,
      align: 'center',
      render: (_, record) => {
        // const mostrar = record.estado === 'SALIDA' ? true :
        //                record.estado === 'ENTRADA' ? false :
        //                record.estado === 'LIBRE' ? false :
        //                (dayjs(record.fechaListado).diff(dayjs(record.entrada), 'days') % filtros.cambiarToallas === 0);
        
        const fechaListadoDate = record.fechaListado+'T00:00:00.000Z';
        const entradaDate = record.Entrada;
      
        const mostrar = record.Estado === 'SALIDA' ? true :
                       record.Estado === 'ENTRADA' ? false :
                       record.Estado === 'LIBRE' ? false :
                       (dayjs(fechaListadoDate).diff(dayjs(entradaDate), 'days') % filtros.cambiarToallas === 0);

                      //  console.log(dayjs(fechaListadoDate).diff(dayjs(entradaDate), 'days'));

        return (
          <div style={{
            backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
            padding: '4px',
            textAlign: 'center'
          }}>
            {mostrar && <span style={{ color: 'green', fontWeight: 'bold' }}>X</span>}
          </div>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 60,
      fixed: 'right',
      render: (_, record) => (
        <div style={{
          backgroundColor: record.esVIP ? '#FFE4E1' : (record.estado === 'SALIDA' ? '#F0F0F0' : 'transparent'),
          padding: '4px'
        }}>
          <Tooltip title="Ver detalles">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalle(record)}
              size="small"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  // Panel de filtros
  const panelFiltros = (
    <Card 
      size="small" 
      style={{ marginBottom: 16, backgroundColor: '#fafafa' }}
      title={
        <Space>
          <FilterOutlined />
          <Text strong>Filtros del Listado</Text>
        </Space>
      }
      extra={
        <Space>
          <Button 
            type="primary" 
            icon={<SearchOutlined />} 
            onClick={generarListado}
            loading={searchLoading}
          >
            Generar Listado
          </Button>
          <Button 
            icon={<PrinterOutlined />} 
            onClick={handlePrint}
            disabled={listado.length === 0}
          >
            Imprimir Reporte
          </Button>
        </Space>
      }
    >
      <Row gutter={[16, 16]}>
        <Col span={4}>
          <Form.Item label="Fecha" style={{ marginBottom: 0 }}>
            <DatePicker
              value={filtros.fecha ? dayjs(filtros.fecha) : null}
              onChange={(date) => handleFiltroChange('fecha', date ? date.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'))}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              allowClear={false}
            />
          </Form.Item>
        </Col>
        
        <Col span={3}>
          <Form.Item label="C. Sábanas" style={{ marginBottom: 0 }}>
            <Input
              type="number"
              value={filtros.cambiarSabanas}
              onChange={(e) => handleFiltroChange('cambiarSabanas', parseInt(e.target.value) || 0)}
              min={0}
              max={7}
              addonAfter="días"
            />
          </Form.Item>
        </Col>
        
        <Col span={3}>
          <Form.Item label="C. Toallas" style={{ marginBottom: 0 }}>
            <Input
              type="number"
              value={filtros.cambiarToallas}
              onChange={(e) => handleFiltroChange('cambiarToallas', parseInt(e.target.value) || 0)}
              min={0}
              max={7}
              addonAfter="días"
            />
          </Form.Item>
        </Col>
        
        <Col span={4}>
          <Form.Item label="Tipo Listado" style={{ marginBottom: 0 }}>
            <Select
              value={filtros.tipoListado}
              onChange={(value) => handleFiltroChange('tipoListado', value)}
            >
              <Option value={0}>Normal</Option>
              <Option value={1}>Agrupado por Rango</Option>
              {informesDisponibles.map(informe => (
                <Option key={informe.value} value={informe.value + 10000}>
                  {informe.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        
        <Col span={10}>
          <Form.Item label="Opciones" style={{ marginBottom: 0 }}>
            <Space wrap size="small">
              <Switch
                checkedChildren="Rango"
                unCheckedChildren="Rango"
                checked={filtros.mostrarRangoGobernanta}
                onChange={(checked) => handleFiltroChange('mostrarRangoGobernanta', checked)}
              />
              {/* <Switch
                checkedChildren="ANB"
                unCheckedChildren="ANB"
                checked={filtros.mostrarANB}
                onChange={(checked) => handleFiltroChange('mostrarANB', checked)}
              />
              <Switch
                checkedChildren="Huésped"
                unCheckedChildren="Huésped"
                checked={filtros.mostrarNombreHuesped}
                onChange={(checked) => handleFiltroChange('mostrarNombreHuesped', checked)}
              />
              <Switch
                checkedChildren="Agrupar"
                unCheckedChildren="Agrupar"
                checked={filtros.saltarPorPiso}
                onChange={(checked) => handleFiltroChange('saltarPorPiso', checked)}
              /> */}
              <Switch
                checkedChildren="Libres"
                unCheckedChildren="Libres"
                checked={filtros.mostrarHabitacionesLibres}
                onChange={(checked) => handleFiltroChange('mostrarHabitacionesLibres', checked)}
              />
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  return (
    <>
      <PageContainer
        title="Gobernanta"
        subtitle={`Listado de habitaciones - ${filtros.fecha}`}
        icon={<AppstoreOutlined />}
        loading={loading}
        onRefresh={generarListado}
        badgeCount={listado.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => message.info('Exportando a PDF...')}
              disabled={listado.length === 0}
            >
              PDF
            </Button>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
              disabled={listado.length === 0}
            >
              Imprimir
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Panel de filtros */}
        {panelFiltros}

        {/* Tabs */}
        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: 16 }}>
          <TabPane 
            tab={
              <span>
                <AppstoreOutlined />
                Listado General ({listado.length})
              </span>
            } 
            key="listado"
          >
            {/* Tabla principal con estilo FastReport */}
            <Table
              columns={columns}
              dataSource={listado}
              rowKey={(record) => `${record.NumHab}`}
              loading={loading}
              scroll={{ x: 1300, y: 500 }}
              size="small"
              // pagination={{ 
              //   pageSize: 50,
              //   showSizeChanger: true,
              //   pageSizeOptions: ['20', '50', '100', '200'],
              //   showQuickJumper: true,
              //   showTotal: (total) => `${total} habitaciones`
              // }}
              bordered
              className="tabla-gobernanta"
              // onChange={(pagination, filters, sorter) => {
              //   console.log(sorter);
    
              //   // Actualizar el estado del orden
              //   setSortInfo({
              //     field: sorter.field || 'NumHab',
              //     order: sorter.order || 'ascend'
              //   });
    
              //   // Ordenar los datos manualmente
              //   if (sorter.field) {
              //     const datosOrdenados = [...listado].sort((a, b) => {
              //       const field = sorter.field;
              //       const order = sorter.order || 'ascend';
        
              //       // Para campos numéricos
              //       if (field === 'NumHab' || field === 'pax') {
              //         console.log(" ordeno por NumHab");
                      
              //         const valA = Number(a[field]) || 0;
              //         const valB = Number(b[field]) || 0;
              //         return order === 'ascend' ? valA - valB : valB - valA;
              //       }
        
              //       // Para campos de fecha
              //       if (field === 'entrada' || field === 'salida') {
              //         const dateA = a[field] ? new Date(a[field]) : new Date(0);
              //         const dateB = b[field] ? new Date(b[field]) : new Date(0);
              //         return order === 'ascend' ? dateA - dateB : dateB - dateA;
              //       }
        
              //       // Para campos de texto
              //       const valA = String(a[field] || '');
              //       const valB = String(b[field] || '');
              //       return order === 'ascend' ? valA.localeCompare(valB) : valB.localeCompare(valA);
              //     });
      
              //     setSortedData(datosOrdenados);
              //   } else {
              //     // Si no hay campo de orden, usar el default
              //     const datosDefault = [...listado].sort((a, b) => a.NumHab - b.NumHab);
              //     setSortedData(datosDefault);
              //   }
              // }}
            
              locale={{
                emptyText: (
                  <Empty 
                    description="No hay habitaciones para mostrar"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )
              }}
            />
          </TabPane>
        </Tabs>

        {/* Resumen de totales al estilo FastReport */}
        {estadisticas && (
          <Card size="small" style={{ marginTop: 16, backgroundColor: '#f9f9f9' }}>
            <div style={{ display: 'flex', gap: 30, flexWrap: 'wrap' }}>
              <div><strong>Salidas:</strong> {estadisticas.salidas}</div>
              <div><strong>Entradas:</strong> {estadisticas.entradas}</div>
              <div><strong>Salida/Entrada:</strong> {estadisticas.salidaEntrada}</div>
              <div><strong>Ocupadas:</strong> {estadisticas.ocupadas}</div>
              <div><strong>Libres:</strong> {estadisticas.libres}</div>
              <div><strong>Bloqueadas:</strong> {estadisticas.bloqueadas}</div>              
            </div>
          </Card>
        )}
      </PageContainer>

      {/* Componente oculto para impresión */}
      <div style={{ display: 'none' }}>
        <ReporteGobernanta
          ref={reporteRef}
          datos={listado}
          filtros={filtros}
          estadisticas={estadisticas}
          hotelInfo={hotelInfo}
        />
      </div>

      {/* Modal de detalles de habitación */}
      <Modal
        title={`Detalles Habitación ${habitacionSeleccionada?.numHab}`}
        open={detalleVisible}
        onCancel={() => setDetalleVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetalleVisible(false)}>
            Cerrar
          </Button>,
          <Button 
            key="print" 
            type="primary" 
            icon={<PrinterOutlined />}
            onClick={() => {
              setDetalleVisible(false);
              message.info('Imprimiendo detalles...');
            }}
          >
            Imprimir
          </Button>
        ]}
        width={600}
      >
        {habitacionSeleccionada && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Habitación" span={2}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '2px 8px' }}>
                {habitacionSeleccionada.numHab}
                {habitacionSeleccionada.esVIP && ' 👑'}
              </Tag>
            </Descriptions.Item>
            
            <Descriptions.Item label="Piso">
              {habitacionSeleccionada.piso || '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label="Rango">
              {habitacionSeleccionada.rango || '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label="Estado" span={2}>
              {habitacionSeleccionada.estado}
            </Descriptions.Item>
            
            <Descriptions.Item label="Ocupantes">
              {habitacionSeleccionada.ocupantes || '-'}
            </Descriptions.Item>
            
            <Descriptions.Item label="Habitación Especial">
              {habitacionSeleccionada.habitacionEspecial || '-'}
            </Descriptions.Item>
            
            {habitacionSeleccionada.entrada && (
              <Descriptions.Item label="Entrada">
                {dayjs(habitacionSeleccionada.entrada).format('DD/MM/YYYY')}
              </Descriptions.Item>
            )}
            
            {habitacionSeleccionada.salida && (
              <Descriptions.Item label="Salida">
                {dayjs(habitacionSeleccionada.salida).format('DD/MM/YYYY')}
              </Descriptions.Item>
            )}
            
            {habitacionSeleccionada.nombreHuesped && (
              <Descriptions.Item label="Huésped" span={2}>
                <Text strong>{habitacionSeleccionada.nombreHuesped}</Text>
              </Descriptions.Item>
            )}
            
            <Descriptions.Item label="Observaciones" span={2}>
              {habitacionSeleccionada.observaciones || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default ListadoGobernanta;